// ═══════════════════════════════════════════════════════════════
// code-editor-io.ts — the @liquidefy/code-editor engine, wired to the
// daemon over the deployment command channel (Tier 2 v2).
// ═══════════════════════════════════════════════════════════════
//
// The code-editor engine is PURE + browser-safe: `createCodeEditor(deps)`
// runs the document model, diff, syntax, search, layout — all in the
// browser, zero deps. The only effecting injection point we use in v1 is
// `fs: FileSystemBackend`, which we implement as a thin proxy over the
// deployment server's `workspace.files.*` dispatch ops (owner-gated,
// path-scoped server-side). That's the same two-plane shape as the rest
// of apps/network: the model is in-browser, effects cross the WS seam.
//
// Not yet injected (honest deferrals): git (we commit via the dedicated
// `workspace.git.commit` op, not the engine's git namespace), pty/lsp/ai
// (Phases E–G), storage/sync. Those land as the surface grows.

import { createCodeEditor } from "@liquidefy/code-editor";
import type { FileSystemBackend, FileStat } from "@liquidefy/code-editor/types";

/** The constructed engine handle — every namespace the package returns. */
export type EditorEngine = ReturnType<typeof createCodeEditor>;

/** The deployment node's dispatch seam (matches DeploymentIOContext.dispatch). */
export type Dispatch = (op: string, payload?: unknown) => Promise<unknown>;

interface WorkspaceMeta {
  readonly path: string;
  readonly size: number;
  readonly updatedAtMs: number;
}

/** Strip a leading slash — our workspace store is project-relative; the
 *  engine may hand us rooted paths. */
const rel = (p: string): string => p.replace(/^\/+/, "");

/**
 * A `FileSystemBackend` backed by `workspace.files.*` dispatch. Every call
 * is an owner-gated, path-sanitized round-trip to the daemon (the server
 * re-validates the path — nothing here is trusted as a security boundary).
 */
export function createWorkspaceFs(dispatch: Dispatch, projectId: string): FileSystemBackend {
  async function listAll(): Promise<readonly WorkspaceMeta[]> {
    const r = (await dispatch("workspace.files.list", { projectId })) as { files?: WorkspaceMeta[] };
    return r?.files ?? [];
  }

  return {
    async readFile(path: string): Promise<string> {
      const r = (await dispatch("workspace.files.read", { projectId, path: rel(path) })) as {
        content?: string | null;
        exists?: boolean;
      };
      if (!r || r.exists === false) throw new Error(`ENOENT: ${path}`);
      return r.content ?? "";
    },

    async writeFile(path: string, content: string): Promise<void> {
      await dispatch("workspace.files.write", { projectId, path: rel(path), content });
    },

    async deleteFile(path: string): Promise<void> {
      await dispatch("workspace.files.delete", { projectId, path: rel(path) });
    },

    async readDir(dirPath: string): Promise<string[]> {
      const prefix = rel(dirPath).replace(/\/+$/, "");
      const files = await listAll();
      const names = new Set<string>();
      for (const f of files) {
        if (prefix && !(f.path === prefix || f.path.startsWith(`${prefix}/`))) continue;
        const rest = prefix ? f.path.slice(prefix.length + 1) : f.path;
        const seg = rest.split("/")[0];
        if (seg) names.add(seg);
      }
      return [...names].sort();
    },

    async stat(path: string): Promise<FileStat> {
      const p = rel(path);
      const files = await listAll();
      const exact = files.find((f) => f.path === p);
      const isDirectory = !exact && files.some((f) => f.path.startsWith(`${p}/`));
      if (!exact && !isDirectory) throw new Error(`ENOENT: ${path}`);
      const when = exact?.updatedAtMs ?? Date.now();
      return {
        size: exact?.size ?? 0,
        isFile: exact !== undefined,
        isDirectory,
        isSymlink: false,
        modifiedAt: when,
        createdAt: when,
        permissions: 0o644,
      };
    },

    // v1: no server→browser push channel for workspace files, so live
    // file-watching is an honest no-op. The editor reloads explicitly
    // after writes; a real watcher rides the event stream in a later pass.
    watch(): { readonly dispose: () => void } {
      return { dispose() {} };
    },
  };
}

/**
 * Construct the code-editor engine for a project, with its filesystem
 * bound to that project's workspace over the dispatch seam.
 */
export function createEditorEngine(dispatch: Dispatch, projectId: string): EditorEngine {
  return createCodeEditor({ fs: createWorkspaceFs(dispatch, projectId) });
}

// ═══════════════════════════════════════════════════════════════
// Git IO (G3) — the per-project git surface for the SCM panel.
// ═══════════════════════════════════════════════════════════════
//
// Gitea is the git source of truth; these proxy the daemon's `workspace.git.*`
// dispatch ops (owner-gated, enforce-classified server-side). Per-file diff
// reuses the code-editor engine's pure text-diff — committed HEAD vs working copy.

export interface GitChange {
  readonly path: string;
  readonly status: "added" | "modified" | "deleted";
}
export interface GitStatus {
  readonly branch: string;
  readonly clean: boolean;
  readonly changes: readonly GitChange[];
}
export interface GitCommitInfo {
  readonly sha: string;
  readonly message: string;
  readonly author: string;
  readonly date: string;
}
export interface GitCommitResult {
  readonly ok?: boolean;
  readonly note?: string;
  readonly sha?: string | null;
  readonly error?: string;
  readonly files?: number;
}

export interface GitIO {
  /** Changed files: working copy (workspace) vs committed HEAD. */
  status(): Promise<GitStatus>;
  /** Recent commits (newest first). */
  log(): Promise<{ branch: string; commits: GitCommitInfo[] }>;
  branches(): Promise<{ default: string; branches: string[] }>;
  /** Checkout: load the committed HEAD into the workspace so the editor shows the repo. */
  pull(): Promise<{ branch: string; files: number }>;
  /** Commit the whole workspace to the project's Gitea repo (build vertical reads from there). */
  commit(message: string, branch?: string): Promise<GitCommitResult>;
  /** Unified diff of one path: committed HEAD ("before") vs working copy ("after"). */
  diff(path: string): Promise<string>;
}

/** Construct the git IO surface for a project over the dispatch seam. The `engine`
 *  supplies the pure text-diff (same one the editor uses). */
export function createGitIO(dispatch: Dispatch, projectId: string, engine: EditorEngine): GitIO {
  return {
    status: () => dispatch("workspace.git.status", { projectId }) as Promise<GitStatus>,
    log: () => dispatch("workspace.git.log", { projectId }) as Promise<{ branch: string; commits: GitCommitInfo[] }>,
    branches: () => dispatch("workspace.git.branches", { projectId }) as Promise<{ default: string; branches: string[] }>,
    pull: () => dispatch("workspace.git.pull", { projectId }) as Promise<{ branch: string; files: number }>,
    commit: (message: string, branch?: string) =>
      dispatch("workspace.git.commit", { projectId, message, branch }) as Promise<GitCommitResult>,
    async diff(path: string): Promise<string> {
      const r0 = rel(path);
      const [head, work] = await Promise.all([
        dispatch("workspace.git.show", { projectId, path: r0 }) as Promise<{ content?: string | null }>,
        dispatch("workspace.files.read", { projectId, path: r0 }) as Promise<{ content?: string | null }>,
      ]);
      const d = engine.diff.compute(head?.content ?? "", work?.content ?? "");
      return d.success ? engine.diff.formatUnified(d.value, 3) : "";
    },
  };
}
